import React from 'react';
import { ArrowRight } from 'lucide-react';
import { PageHero, Reveal, SectionTitle, Btn, Kicker } from '../components/common';
import { img, leadership, partners, board, civicPartners, garden, conservancyIntro, conservancyRoles, conservancyCommitment } from '../mock/mock';

const Conservancy = () => {
  return (
    <div>
      <PageHero image={img.volunteersPlanting} kicker="The Conservancy" title="Stewards of a shared green future" sub="The nonprofit Conservancy leads the creation, funding and care of the garden on behalf of our community." />

      {/* About */}
      <section id="about" className="py-24 bg-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img src={img.volunteersShovels} alt="Volunteers" className="w-full h-[480px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={150}>
            <SectionTitle kicker="About the Conservancy" title="Role & Responsibility of the Conservancy" />
            {conservancyIntro.map((p, i) => (
              <p key={i} className={`${i === 0 ? 'mt-6' : 'mt-4'} text-ink/70 leading-relaxed`}>{p}</p>
            ))}
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[['501(c)(3)', 'Nonprofit'], ['40', 'Acres'], ['$5.4M', 'Investment']].map(([a, b]) => (
                <div key={b} className="p-4 rounded-xl bg-white border border-sand text-center">
                  <div className="font-display text-2xl text-forest">{a}</div>
                  <div className="text-xs text-ink/55 mt-1">{b}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Roles & Responsibilities (real content) */}
      <section className="py-24 bg-forest text-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center light kicker="Our Roles & Responsibilities" title="How the Conservancy serves the Garden" /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {conservancyRoles.map((r, i) => (
              <Reveal key={r.title} delay={(i % 3) * 100}>
                <div className="p-8 rounded-2xl bg-forest-dark/60 border border-forest-light/30 h-full">
                  <h3 className="font-display text-xl text-gold-light">{r.title}</h3>
                  <p className="mt-3 text-cream/70 text-sm leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={150}>
            <div className="mt-12 max-w-3xl mx-auto text-center bg-forest-dark/50 border border-forest-light/30 rounded-2xl p-8">
              <h3 className="font-display text-2xl text-white">Our Commitment</h3>
              <p className="mt-4 text-cream/80 leading-relaxed">{conservancyCommitment}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Leadership */}
      <section id="leadership" className="py-24 bg-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="Leadership" title="Meet the team growing the garden" /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {leadership.map((l, i) => (
              <Reveal key={l.name} delay={i * 100}>
                <div className="text-center bg-white rounded-2xl p-7 shadow-lg border border-sand h-full">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-forest-light to-forest flex items-center justify-center">
                    <span className="font-display text-3xl text-gold-light">{l.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}</span>
                  </div>
                  <h3 className="font-display text-lg text-forest mt-5">{l.name}</h3>
                  <p className="text-gold text-xs font-600 tracking-wide uppercase mt-1">{l.role}</p>
                  <p className="text-ink/60 text-sm mt-3 leading-relaxed">{l.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Board */}
      <section id="board" className="py-24 bg-forest text-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center light kicker="Board of Directors" title="Meet the Board" sub="A dedicated group of civic, horticultural and community leaders guiding the Conservancy." /></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {board.map((m, i) => (
              <Reveal key={m.name} delay={(i % 4) * 90}>
                <div className="group bg-forest-dark/50 rounded-2xl overflow-hidden border border-forest-light/25 hover:border-gold/60 transition-colors h-full">
                  <div className="relative h-64 overflow-hidden">
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-forest-dark/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                      <p className="text-cream/90 text-sm leading-relaxed">{m.bio}</p>
                    </div>
                  </div>
                  <div className="p-5 text-center">
                    <h3 className="font-display text-lg text-white">{m.name}</h3>
                    <p className="text-gold-light text-xs font-600 tracking-wide uppercase mt-1">{m.role}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <p className="text-center text-cream/50 text-xs mt-10 max-w-2xl mx-auto">Board portraits and names shown are representative placeholders and will be updated as the Conservancy publishes its official roster.</p>
          </Reveal>
        </div>
      </section>

      {/* Civic Partners */}
      <section className="py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="Founding Civic Leadership" title="Champions behind the garden" sub="The Roderick Gay Botanical Garden is made possible through the vision of dedicated public leaders and the City of College Park & Fulton County partnership." /></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {civicPartners.map((c, i) => (
              <Reveal key={c.name} delay={i * 120}>
                <div className="h-full bg-white rounded-2xl p-8 shadow-lg border border-sand text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-gold to-gold-light flex items-center justify-center">
                    <span className="font-display text-2xl text-white">{c.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}</span>
                  </div>
                  <h3 className="font-display text-xl text-forest mt-5">{c.name}</h3>
                  <p className="text-gold text-xs font-600 tracking-wide uppercase mt-1">{c.role}</p>
                  <p className="text-ink/60 text-sm mt-3 leading-relaxed">{c.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* Partners */}
      <section id="partners" className="py-20 bg-sand scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <Reveal><Kicker>Our Partners</Kicker>
            <h2 className="font-display text-3xl text-forest mt-4 mb-10">Growing together</h2>
          </Reveal>
          <div className="flex flex-wrap justify-center gap-4">
            {partners.map((p, i) => (
              <Reveal key={p} delay={i * 60}>
                <div className="px-6 py-4 rounded-xl bg-white border border-sand text-forest font-500 text-sm shadow-sm">{p}</div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12"><Btn to="/give" variant="outlineDark">Partner with us <ArrowRight className="w-4 h-4" /></Btn></div>
        </div>
      </section>
    </div>
  );
};

export default Conservancy;
